import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { ListComponentGeneric } from '../../../../ui/generics';
import { OrdersService, OrdersServiceErrorResolverService } from '../../core/services';
import { NotificationService } from '../../../../core/services';
import { IDeliveryOrder } from '../../core/interfaces/IDeliveryOrder';
import { SuppliersService } from '../../../suppliers/core/services/SuppliersService';
import { debounceTime, finalize, switchMap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { DeliveryOrderStatuses } from '../../../../core/enums';
import { OrderTypes } from '../../core/enums';
import { ISimpleListItem } from '../../../../core/interfaces/ISimpleListItem';
import { IOrderStatusCounts } from '../../../../core/interfaces';
import { SendOrderMailModalComponent } from '../send-order-mail-modal/send-order-mail-modal.component';

@Component({
    selector: 'app-orders-list',
    templateUrl: './orders-list.component.html',
    styleUrls: ['./orders-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OrdersListComponent extends ListComponentGeneric<IDeliveryOrder> implements OnInit {

  @ViewChild('sendMailModal') sendMailModal: SendOrderMailModalComponent;
  @Input() orderType = OrderTypes.MIXED;
  @Output() selectedDocumentKeysChange = new EventEmitter<string[]>();
  @Output() statusCountsChange = new EventEmitter<IOrderStatusCounts>();
  public orderTypes = OrderTypes;
  public isSuppliersLoading = false;
  public searchSupplierChange$ = new Subject();
  public selectedLastSuppliers: ISimpleListItem[] = [];
  public suppliersList: ISimpleListItem[];
  public selectedDocumentKeys: string[] = [];
  public selectedStatuses: Array<keyof typeof DeliveryOrderStatuses> = [];
  public paidFilter: string[] | string | null = null;
  public invoiceFilter: string[] | string | null = null;
  public paidFilters = [
    { text: 'Bezahlt', value: 'true', byDefault: false },
    { text: 'Nicht bezahlt', value: 'false', byDefault: false },
  ];
  public invoiceFilters = [
    { text: 'Rechnung hochgeladen', value: 'true', byDefault: false },
    { text: 'Rechnung nicht hochgeladen', value: 'false', byDefault: false },
  ];
  public readonly deliveryOrderStatuses = DeliveryOrderStatuses;
  public readonly statusKeys: Array<keyof typeof DeliveryOrderStatuses> = [
    'Open',
    'PartlyFinished',
    'Finished',
    'Canceled',
  ];
  public listReady = false;

  searchColumns = [
    'id',
    'orderProducts.id',
    'orderProducts.number',
    'orderProducts.name',
    'supplier.name',
  ];

  protected readonly exportedTableVisibleName = 'Bestellliste Großgebinde';

  constructor(
    protected _service: OrdersService,
    protected _cdr: ChangeDetectorRef,
    protected _notifyService: NotificationService,
    private _supplierService: SuppliersService,
    protected renderer: Renderer2,
    protected _errorResolver: OrdersServiceErrorResolverService,
  ) {
    super(_service, _cdr, _notifyService, renderer, _errorResolver);
  }

  ngOnInit(): void {
    const getSuppliers = (name: string) =>
      this._supplierService.getSimpleList(name);

    const suppliersOptionList$: Observable<ISimpleListItem[]> = this.searchSupplierChange$
      .asObservable()
      .pipe(debounceTime(500))
      .pipe(switchMap(getSuppliers));
    suppliersOptionList$.subscribe(data => {
      this.suppliersList = data;
      this.isSuppliersLoading = false;
      this._cdr.detectChanges();
    });
  }

  public get unfilteredTotal(): number {
    const counts = this.data.statusCounts;
    if (!counts) {
      return this.data.info.totalResults;
    }
    return (counts.open ?? 0)
      + (counts.partlyFinished ?? 0)
      + (counts.finished ?? 0)
      + (counts.canceled ?? 0);
  }

  public get hasActiveFilters(): boolean {
    return !!(
      this.currentSearch.trim()
      || this.selectedLastSuppliers.length
      || this.selectedStatuses.length
      || this.hasColumnFilter(this.paidFilter)
      || this.hasColumnFilter(this.invoiceFilter)
    );
  }

  public onSearchSupplier(value: string): void {
    this.isSuppliersLoading = true;
    this.searchSupplierChange$.next(value);
  }

  public onStatusFilterChange(): void {
    this.reloadKeepingColumnFilters(1);
  }

  public resetFilters(): void {
    this.currentSearch = '';
    this.selectedLastSuppliers = [];
    this.selectedStatuses = [];
    this.paidFilter = null;
    this.invoiceFilter = null;
    this.paidFilters = this.paidFilters.map(el => ({ ...el, byDefault: false }));
    this.invoiceFilters = this.invoiceFilters.map(el => ({ ...el, byDefault: false }));
    this.load(1, this.lastLoadParams.pageSize || this.pageSize, this.lastLoadParams.sortField, this.lastLoadParams.sortOrder, [], []);
  }

  load(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filter: { key: string; value: string[] }[],
    search: { key: string; value: string[] }[],
    additionalFilters: { key: string; value: string[] }[] = [],
  ): void {
    filter = [
      ...filter.filter(f => f.key !== 'supplier.name' && f.key !== 'status' && f.key !== 'orderType'),
      ...this.selectedLastSuppliers.map(x => ({
        key: 'supplier.name',
        value: [x.value]
      })),
      ...this.selectedStatuses.map(status => ({
        key: 'status',
        value: [status]
      })),
    ];

    if (!sortField) {
      sortField = 'id';
    }

    if (!sortOrder) {
      sortOrder = 'descend';
    }

    this.lastLoadParams = {
      pageIndex,
      pageSize,
      sortField,
      sortOrder,
      filter,
      search,
      additionalFilters
    };
    filter = [...filter, ...this.predefindedFilters];

    if (this.orderType !== OrderTypes.MIXED) {
      filter = [...filter, { key: 'orderType', value: [this.orderType] }];
    }

    if (this.keepStateDirective) {
      this.keepStateDirective.item = {
        ...this.lastLoadParams
      };
    }
    this.loading = true;
    this._listService.getQueryableAll(pageIndex, pageSize, sortField, sortOrder, filter, search, additionalFilters).pipe(
    ).subscribe(res => {
      this.loading = false;
      this.data = res;
      this.listReady = true;
      if (res.statusCounts) {
        this.statusCountsChange.emit(res.statusCounts);
      }
      this._cdr.detectChanges();
    }, this.parseError);
  }

  deleteElement = (id: number) => {
    this.loading = true;
    this._service.cancel(id).pipe(finalize(() => this.loading = false)).subscribe(() => {
      this.onRefreshList(false);
    }, this.parseError);
  }

  public compareSimpleList(val1: ISimpleListItem, val2: ISimpleListItem): boolean {
    return val1?.id?.toString() === val2?.id?.toString();
  }

  public openSupplierMail(id: number): void {
    this.sendMailModal?.open(id);
  }

  protected refreshCheckedStatus(): void {
    super.refreshCheckedStatus();
    this.selectedDocumentKeys = this.data.results.filter(value => value.documentKey && this.setOfCheckedId.has(value.id)).map(value => (value.documentKey));
    this.selectedDocumentKeysChange.emit(this.selectedDocumentKeys);
    this._cdr.detectChanges();
  }

  public onStatusChange(orderId: number, status: string): void {
    this._service.changeOrderStatus(orderId, status)
      .subscribe({
        next: () => {
          this._notifyService.pushSuccess('Erfolgreich', 'Bestellstatus aktualisiert');
          this.onRefreshList(false);
        },
        error: error => {
          this.onRefreshList(false);
          this.parseError(error);
        }
      });
  }

  private hasColumnFilter(value: string[] | string | null): boolean {
    return Array.isArray(value) ? value.length > 0 : !!value;
  }

  private reloadKeepingColumnFilters(pageIndex: number): void {
    const columnFilters = (this.lastLoadParams.filter || []).filter(
      f => f.key === 'isPaid' || f.key === 'isInvoiceUploaded'
    );
    this.load(
      pageIndex,
      this.lastLoadParams.pageSize || this.pageSize,
      this.lastLoadParams.sortField,
      this.lastLoadParams.sortOrder,
      columnFilters,
      this.currentSearch.trim()
        ? this.searchColumns.map(el => ({ key: el, value: [this.currentSearch] }))
        : []
    );
  }
}
